import { useState, type FormEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, X, Server, Trash2, ToggleLeft, ToggleRight, RefreshCw, Check, AlertCircle } from 'lucide-react';
import api from '../lib/api';

interface McpServer {
  id: string;
  projectId: string;
  name: string;
  url: string;
  type: 'api_catalog' | 'cortex' | 'custom' | 'swagger';
  enabled: boolean;
  headers: Record<string, string> | null;
  createdAt: string;
  updatedAt: string;
}

interface CreateMcpServerDto {
  name: string;
  url: string;
  type: string;
  apiKey?: string;
  enabled?: boolean;
}

function useMcpServers(projectId: string) {
  return useQuery<McpServer[]>({
    queryKey: ['projects', projectId, 'mcp-servers'],
    queryFn: async () => {
      const res = await api.get(`/api/projects/${projectId}/mcp-servers`);
      return res.data;
    },
  });
}

function useCreateMcpServer(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateMcpServerDto) => {
      const res = await api.post(`/api/projects/${projectId}/mcp-servers`, data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'mcp-servers'] });
    },
  });
}

function useDeleteMcpServer(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/projects/${projectId}/mcp-servers/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'mcp-servers'] });
    },
  });
}

function useToggleMcpServer(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, enabled }: { id: string; enabled: boolean }) => {
      const res = await api.patch(`/api/projects/${projectId}/mcp-servers/${id}`, { enabled });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'mcp-servers'] });
    },
  });
}

function useTestMcpServer(projectId: string) {
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post(`/api/projects/${projectId}/mcp-servers/${id}/test`);
      return res.data as { success: boolean; message: string };
    },
  });
}

const TYPE_BADGES: Record<string, string> = {
  api_catalog: 'bg-blue-100 text-blue-700',
  cortex: 'bg-purple-100 text-purple-700',
  custom: 'bg-slate-100 text-slate-700',
};

const TYPE_LABELS: Record<string, string> = {
  api_catalog: 'API Catalog',
  cortex: 'Cortex',
  custom: 'Custom',
};

function normalizeServerType(type: McpServer['type']): 'api_catalog' | 'cortex' | 'custom' {
  if (type === 'swagger') return 'api_catalog';
  return type;
}

interface ProjectMcpServersProps {
  projectId: string;
}

export default function ProjectMcpServers({ projectId }: ProjectMcpServersProps) {
  const { data: servers, isLoading } = useMcpServers(projectId);
  const createServer = useCreateMcpServer(projectId);
  const deleteServer = useDeleteMcpServer(projectId);
  const toggleServer = useToggleMcpServer(projectId);
  const testServer = useTestMcpServer(projectId);

  const [showModal, setShowModal] = useState(false);
  const [testResults, setTestResults] = useState<Record<string, { success: boolean; message: string }>>({});

  // Form state
  const [formName, setFormName] = useState('');
  const [formUrl, setFormUrl] = useState('');
  const [formType, setFormType] = useState<string>('api_catalog');
  const [formApiKey, setFormApiKey] = useState('');
  const [formEnabled, setFormEnabled] = useState(true);

  const resetForm = () => {
    setFormName('');
    setFormUrl('');
    setFormType('api_catalog');
    setFormApiKey('');
    setFormEnabled(true);
  };

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault();
    if (!formName.trim() || !formUrl.trim()) return;
    await createServer.mutateAsync({
      name: formName.trim(),
      url: formUrl.trim(),
      type: formType,
      apiKey: formApiKey.trim() || undefined,
      enabled: formEnabled,
    });
    resetForm();
    setShowModal(false);
  };

  const handleTest = async (id: string) => {
    try {
      const result = await testServer.mutateAsync(id);
      setTestResults(prev => ({ ...prev, [id]: result }));
    } catch {
      setTestResults(prev => ({ ...prev, [id]: { success: false, message: 'Connection failed' } }));
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this MCP server?')) return;
    await deleteServer.mutateAsync(id);
  };

  if (isLoading) {
    return <div className="text-sm text-slate-400">Loading MCP servers...</div>;
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h3 className="font-semibold text-slate-900">MCP Servers</h3>
        <button
          onClick={() => setShowModal(true)}
          className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800"
        >
          <Plus className="h-4 w-4" />
          Add Server
        </button>
      </div>

      {(!servers || servers.length === 0) ? (
        <div className="rounded-xl border border-dashed border-slate-300 p-8 text-center">
          <Server className="mx-auto mb-3 h-10 w-10 text-slate-300" />
          <p className="text-sm text-slate-500">No MCP servers configured for this project.</p>
          <p className="mt-1 text-xs text-slate-400">Add API Catalog, Cortex, or custom MCP servers to extend agent capabilities.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {servers.map(server => {
            const normalizedType = normalizeServerType(server.type);
            const testResult = testResults[server.id];
            return (
              <div key={server.id} className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`h-2.5 w-2.5 rounded-full ${server.enabled ? 'bg-green-500' : 'bg-slate-300'}`} />
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-900">{server.name}</span>
                        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${TYPE_BADGES[normalizedType] || TYPE_BADGES.custom}`}>
                          {TYPE_LABELS[normalizedType] || TYPE_LABELS.custom}
                        </span>
                      </div>
                      <p className="mt-0.5 text-xs text-slate-400 font-mono">{server.url}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleTest(server.id)}
                      disabled={testServer.isPending}
                      className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                      title="Test connection"
                    >
                      <RefreshCw className={`h-4 w-4 ${testServer.isPending ? 'animate-spin' : ''}`} />
                    </button>
                    <button
                      onClick={() => toggleServer.mutate({ id: server.id, enabled: !server.enabled })}
                      className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                      title={server.enabled ? 'Disable' : 'Enable'}
                    >
                      {server.enabled ? <ToggleRight className="h-4 w-4 text-green-600" /> : <ToggleLeft className="h-4 w-4" />}
                    </button>
                    <button
                      onClick={() => handleDelete(server.id)}
                      className="rounded-lg p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                {testResult && (
                  <div className={`mt-2 flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs ${
                    testResult.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                  }`}>
                    {testResult.success ? <Check className="h-3.5 w-3.5" /> : <AlertCircle className="h-3.5 w-3.5" />}
                    {testResult.message}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white shadow-lg">
            <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
              <h2 className="text-lg font-semibold text-slate-900">Add MCP Server</h2>
              <button onClick={() => { resetForm(); setShowModal(false); }} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleCreate} className="p-6 space-y-4">
              <div>
                <label htmlFor="mcp-name" className="mb-1.5 block text-sm font-medium text-slate-700">Name</label>
                <input
                  id="mcp-name"
                  type="text"
                  required
                  value={formName}
                  onChange={e => setFormName(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-slate-500 focus:outline-none"
                  placeholder="e.g. cortex-api-catalog"
                />
              </div>
              <div>
                <label htmlFor="mcp-url" className="mb-1.5 block text-sm font-medium text-slate-700">URL</label>
                <input
                  id="mcp-url"
                  type="url"
                  required
                  value={formUrl}
                  onChange={e => setFormUrl(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-slate-500 focus:outline-none font-mono"
                  placeholder="https://cortex.aldan.cloud"
                />
              </div>
              <div>
                <label htmlFor="mcp-type" className="mb-1.5 block text-sm font-medium text-slate-700">Type</label>
                <select
                  id="mcp-type"
                  value={formType}
                  onChange={e => setFormType(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-slate-500 focus:outline-none"
                >
                  <option value="api_catalog">API Catalog</option>
                  <option value="cortex">Cortex</option>
                  <option value="custom">Custom</option>
                </select>
              </div>
              <div>
                <label htmlFor="mcp-key" className="mb-1.5 block text-sm font-medium text-slate-700">
                  API Key <span className="text-slate-400">(optional)</span>
                </label>
                <input
                  id="mcp-key"
                  type="password"
                  value={formApiKey}
                  onChange={e => setFormApiKey(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-slate-500 focus:outline-none"
                  placeholder="Bearer token for authentication"
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={formEnabled}
                  onChange={e => setFormEnabled(e.target.checked)}
                  className="rounded border-slate-300"
                />
                Enabled
              </label>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => { resetForm(); setShowModal(false); }}
                  className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createServer.isPending || !formName.trim() || !formUrl.trim()}
                  className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
                >
                  {createServer.isPending ? 'Adding...' : 'Add Server'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
